<template>
  <div v-if="hasSecondaryMenu" class="main-nav secondary-menu">
    <account-context @toggle-accounts="toggleAccountModal" />
    <transition-group name="menu-list" tag="ul" class="menu vertical">
      <div class="group-board-types-context" key="group-board-types">
        <span class="legend">{{ $t('SIDEBAR.TYPE_BOARD') }}:</span>
        <secondary-nav-item
          v-for="menuItem in accessibleMenuItems"
          :key="menuItem.toState"
          :menu-item="menuItem"
          :is-active="menuItem.toStateName == currentBoardRouteName"
        />

      </div>
      
      <secondary-nav-item
        v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
        @add-product-label="showAddProductLabelPopup"
        @add-topic-label="showAddTopicLabelPopup"
        @add-concurrent-label="showAddConcurrentLabelPopup"
      />
    </transition-group>
  </div>
</template>
<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import {
  isMenuItemAccessible,
  toBCP47Locale,
} from '../../../helper/menuItemVisibility';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
  },
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    productLabels: {
      type: Array,
      default: () => [],
    },
    topicLabels: {
      type: Array,
      default: () => [],
    },
    concurrentLabels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentRole: {
      type: String,
      default: '',
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isEmbedding: 'isEmbedding',
      typeAccount: 'getCurrentTypeAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      isOpportunityPipelinesManagementAllowed:
        'accounts/isOpportunityPipelinesManagementAllowed',
    }),
    hasSecondaryMenu() {
      if (this.currentRole == 'embed' || this.isEmbedding) {
        return false;
      }
      return this.menuConfig.menuItems && this.menuConfig.menuItems.length;
    },
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    menuVisibilityContext() {
      return {
        currentRole: this.currentRole,
        typeAccount: this.typeAccount,
        currentUser: this.currentUser,
        isOnChatwootCloud: this.isOnChatwootCloud,
        globalConfig: this.globalConfig,
        accountId: this.accountId,
        isFeatureEnabledonAccount: this.isFeatureEnabledonAccount,
        isOpportunityPipelinesManagementAllowed: this
          .isOpportunityPipelinesManagementAllowed,
      };
    },
    accessibleMenuItems() {
      if (!this.currentRole) {
        return [];
      }
      const items = this.menuConfig.menuItems.filter(menuItem =>
        isMenuItemAccessible(menuItem, this.menuVisibilityContext)
      );

      if (!this.menuConfig.sortAlphabetically) {
        return items;
      }

      // Sorted by the translated label so the order is alphabetical in every
      // locale, not only in the one the source array happens to be written in.
      // Items flagged `pinToEnd` (e.g. Applications) skip that ordering and
      // always render after every alphabetically sorted item.
      const locale = toBCP47Locale(this.$i18n.locale);
      return [...items].sort((a, b) => {
        if (!!a.pinToEnd !== !!b.pinToEnd) {
          return a.pinToEnd ? 1 : -1;
        }
        if (a.pinToEnd) {
          return 0;
        }
        return this.$t(`SIDEBAR.${a.label}`).localeCompare(
          this.$t(`SIDEBAR.${b.label}`),
          locale,
          { sensitivity: 'base' }
        );
      });
    },
    boardType(){
      return this.$route.params.boardType ? `board-${this.$route.params.boardType}` : window.location.pathname.split('/')[4];
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
            qualityRating: inbox.quality_rating,
          }))
          .sort((a, b) => {
            const labelA = (a.label || '').toString().toLowerCase();
            const labelB = (b.label || '').toString().toLowerCase();

            if (labelA > labelB) return 1;
            if (labelA < labelB) return -1;
            return 0;
          }),
      };
    },
    boardInboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/${this.boardType}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
            qualityRating: inbox.quality_rating,
          }))
          .sort((a, b) => {
            const labelA = (a.label || '').toString().toLowerCase();
            const labelB = (b.label || '').toString().toLowerCase();

            if (labelA > labelB) return 1;
            if (labelA < labelB) return -1;
            return 0;
          }),
      };
    },
    labelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
        })),
      };
    },
    productLabelSection() {
      return {
        icon: 'tag',
        label: 'PRODUCTS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_PRODUCT',
        key: 'product',
        toState: frontendURL(
          `accounts/${this.accountId}/settings/labels/list`,
          { tab: 'products' }
        ),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddProductLabel',
        children: this.productLabels
          .filter(productLabel => productLabel.show_on_sidebar)
          .map(productLabel => ({
            id: productLabel.id,
            label: productLabel.title,
            color: productLabel.color,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/product_label/${productLabel.title}`
            ),
          })),
      };
    },
    topicLabelSection() {
      return {
        icon: 'tag',
        label: 'TOPICS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TOPIC',
        key: 'topic',
        toState: frontendURL(
          `accounts/${this.accountId}/settings/labels/list`,
          { tab: 'topics' }
        ),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddTopicLabel',
        children: this.topicLabels
          .filter(topicLabel => topicLabel.show_on_sidebar)
          .map(topicLabel => ({
            id: topicLabel.id,
            label: topicLabel.title,
            color: topicLabel.color,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/topic_label/${topicLabel.title}`
            ),
          })),
      };
    },
    concurrentLabelSection() {
      return {
        icon: 'tag',
        label: 'CONCURRENTS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_CONCURRENT',
        key: 'concurrent',
        toState: frontendURL(
          `accounts/${this.accountId}/settings/labels/list`,
          { tab: 'concurrents' }
        ),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddConcurrentLabel',
        children: this.concurrentLabels
          .filter(concurrentLabel => concurrentLabel.show_on_sidebar)
          .map(concurrentLabel => ({
            id: concurrentLabel.id,
            label: concurrentLabel.title,
            color: concurrentLabel.color,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/concurrent_label/${concurrentLabel.title}`
            ),
          })),
      };
    },
    boardLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        key: 'label',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/${this.boardType}/label/${label.title}`
          ),
        })),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    boardTeamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/${this.boardType}/team/${team.id}`
          ),
        })),
      };
    },
    boardStatusSection(){
      return {
        icon: 'people-team',
        label: 'STATUS',
        hasSubMenu: true,
        key: 'status',
        children: ['pending', 'snoozed', 'open', 'resolved'].map(status => ({
          id: status,
          label: this.$t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status}.TEXT`),
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/${this.boardType}/status/${status}`
          ),
        })),
      };

    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    },
    additionalSecondaryMenuItems() {
      let conversationMenuItems = [
        this.inboxSection,
        this.labelSection,
        this.productLabelSection,
        this.topicLabelSection,
        this.concurrentLabelSection,
      ];
      
      let boardMenuItems = [this.boardLabelSection];
      if(this.currentBoardRouteName != 'board_inboxes'){
        boardMenuItems = [this.boardInboxSection, ...boardMenuItems];
      }
      if(this.currentBoardRouteName != 'board_status'){
        boardMenuItems = [this.boardStatusSection, ...boardMenuItems];
      }
      let contactMenuItems = [this.contactLabelSection];
      if (this.teams.length) {
        conversationMenuItems = [this.teamSection, ...conversationMenuItems];
        if(this.currentBoardRouteName != 'board_teams'){
          boardMenuItems = [this.boardTeamSection, ...boardMenuItems];
        }
      }
      if (this.customViews.length) {
        conversationMenuItems = [this.foldersSection, ...conversationMenuItems];
      }
      if (this.contactCustomViews.length) {
        contactMenuItems = [this.contactSegmentsSection, ...contactMenuItems];
      }
      return {
        conversations: conversationMenuItems,
        boards: boardMenuItems,
        contacts: contactMenuItems,
      };
    },
    currentBoardRouteName(){
      if(this.$route.params?.boardType){
        return `board_${this.$route.params.boardType}`;
      }
      return this.$route.name;
    }
  },
  methods: {
    showAddLabelPopup() {
      this.$emit('add-label');
    },
    showAddProductLabelPopup() {
      this.$emit('add-product-label');
    },
    showAddTopicLabelPopup() {
      this.$emit('add-topic-label');
    },
    showAddConcurrentLabelPopup() {
      this.$emit('add-concurrent-label');
    },
    toggleAccountModal() {
      this.$emit('toggle-accounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~dashboard/assets/scss/woot';

.secondary-menu {
  display: flex;
  flex-direction: column;
  background: var(--b-50);
  border-right: 1px solid var(--s-50);
  height: 100%;
  width: 20rem;
  flex-shrink: 0;
  overflow-y: hidden;
  position: unset;

  &:hover {
    overflow-y: hidden;
  }

  .menu {
    padding: var(--space-small);
    overflow-y: auto;
  }
  .group-board-types-context{
    border-radius: var(--border-radius-normal);
    border: 1px solid var(--color-border);
    font-size: var(--font-size-mini);
    padding: var(--space-small);
  }
}
</style>
